import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ShieldCheck, Wrench, Anchor, Factory, Sparkles, Compass } from "lucide-react";
import { HOME } from "../constants/testIds";
import { COMPANY, STATS, SERVICES, PROCESS, BRANDS } from "../lib/site-data";

const Hero = () => (
  <section
    data-testid={HOME.hero}
    className="relative overflow-hidden bg-[color:var(--surface-bg)]"
  >
    <div className="absolute inset-0 grid-noise opacity-60 pointer-events-none" />
    <div className="container-tg relative pt-20 pb-24 lg:pt-28 lg:pb-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-7 reveal">
          <div className="overline mb-6 inline-flex items-center gap-2">
            <span className="w-8 h-0.5 bg-[color:var(--brand-yellow)]" />
            Marine OEM • Est. 1998
          </div>
          <h1 className="font-display font-extrabold text-[color:var(--brand-navy)] leading-[0.95] tracking-tighter text-5xl sm:text-6xl lg:text-7xl">
            Precision marine <br />
            <span className="relative inline-block">
              manufacturing
              <span className="absolute -bottom-2 left-0 right-0 h-3 bg-[color:var(--brand-yellow)] -z-10" />
            </span>
            <br />
            built to last.
          </h1>
          <p className="mt-8 max-w-xl text-lg text-slate-600 leading-relaxed">
            For 25+ years Tecnografic has designed, engineered and manufactured
            custom dash panels, switch panels and marine components for the
            world's leading boat builders — out of our 13,000 sq ft facility in
            Fort Lauderdale, FL.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to="/contact" data-testid={HOME.heroCta} className="btn-primary">
              Start Your Project <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/portfolio" data-testid={HOME.heroCtaSecondary} className="btn-ghost">
              View Our Work <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className="lg:col-span-5 reveal" style={{ animationDelay: "120ms" }}>
          <div className="relative">
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-[color:var(--brand-yellow)] -z-10" />
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1605281317010-fe5ffe798166?w=1200&q=80"
                alt="Luxury yacht helm station"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[color:var(--brand-navy)]/90 to-transparent p-6">
                <div className="text-[color:var(--brand-yellow)] text-xs uppercase tracking-[0.22em] font-bold mb-2">
                  Featured Build
                </div>
                <div className="text-white font-display text-xl font-bold">
                  Custom OEM Helm Dashboard
                </div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white border border-[color:var(--surface-border)] p-5 shadow-xl max-w-[220px]">
              <div className="text-3xl font-display font-extrabold text-[color:var(--brand-navy)]">5 yr</div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">
                Min. warranty against fade & delamination
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const TrustBar = () => (
  <section data-testid={HOME.trustBar} className="bg-[color:var(--brand-navy)] text-white">
    <div className="container-tg py-12">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12">
        {STATS.map((s) => (
          <div key={s.label} className="flex flex-col border-l-2 border-[color:var(--brand-yellow)] pl-4">
            <span className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight">
              {s.value}
            </span>
            <span className="text-[11px] sm:text-xs uppercase tracking-widest text-white/70 mt-2">
              {s.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ServiceCard = ({ s, large }) => (
  <Link
    to="/services"
    className={`group relative overflow-hidden bg-white border border-[color:var(--surface-border)] transition-all duration-300 hover:border-[color:var(--brand-navy)]/30 hover:shadow-xl ${
      large ? "md:col-span-7" : "md:col-span-5"
    }`}
  >
    <div className={`relative overflow-hidden ${large ? "aspect-[16/10]" : "aspect-[4/3]"}`}>
      <img
        src={s.image}
        alt={s.title}
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--brand-navy)]/70 via-transparent to-transparent" />
      <div className="absolute top-4 right-4 w-10 h-10 bg-[color:var(--brand-yellow)] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <ArrowUpRight className="w-5 h-5 text-[color:var(--brand-navy)]" />
      </div>
    </div>
    <div className="p-6">
      <div className="overline text-[color:var(--brand-navy)] mb-2">
        {String(SERVICES.findIndex((x) => x.slug === s.slug) + 1).padStart(2, "0")}
      </div>
      <h3 className="font-display text-2xl font-bold text-[color:var(--brand-navy)] mb-2">
        {s.title}
      </h3>
      <p className="text-sm text-slate-600 leading-relaxed line-clamp-3">{s.short}</p>
    </div>
  </Link>
);

const ServicesGrid = () => (
  <section data-testid={HOME.servicesGrid} className="container-tg py-24 lg:py-32">
    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
      <div>
        <div className="overline mb-4">What we do</div>
        <h2 className="font-display text-4xl lg:text-5xl font-extrabold text-[color:var(--brand-navy)] tracking-tighter max-w-2xl">
          A complete marine manufacturing studio under one roof.
        </h2>
      </div>
      <Link to="/services" className="btn-ghost shrink-0">
        All services <ArrowRight className="w-4 h-4" />
      </Link>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
      <ServiceCard s={SERVICES.find((x) => x.slug === "oem")} large />
      <ServiceCard s={SERVICES.find((x) => x.slug === "design")} />
      <ServiceCard s={SERVICES.find((x) => x.slug === "manufacture")} />
      <ServiceCard s={SERVICES.find((x) => x.slug === "hydro-dip")} />
      <ServiceCard s={SERVICES.find((x) => x.slug === "accessorize")} large />
      <ServiceCard s={SERVICES.find((x) => x.slug === "aftermarket")} />
    </div>
  </section>
);

const Capabilities = () => {
  const items = [
    { icon: Compass, title: "Design & Engineering", body: "SolidWorks®, AutoCAD® and 25 years of in-house expertise." },
    { icon: Factory, title: "CNC Manufacturing", body: "Milling, routing, laser cutting and rotary engraving." },
    { icon: Sparkles, title: "Hydrographic Dipping", body: "Sole NA licensee of Taica CUBIC printing. 1,000+ patterns." },
    { icon: Wrench, title: "Paint & Print", body: "500 marine-grade urethane colors, silk screening, sublimation." },
    { icon: ShieldCheck, title: "UV + Salt Resistance", body: "Built for the harshest marine environment." },
    { icon: Anchor, title: "OEM Trusted", body: "Supplier to the world's largest boat builders since 1998." },
  ];
  return (
    <section data-testid={HOME.capabilities} className="bg-white border-y border-[color:var(--surface-border)]">
      <div className="container-tg py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="overline mb-4">Capabilities</div>
            <h2 className="font-display text-4xl lg:text-5xl font-extrabold text-[color:var(--brand-navy)] tracking-tighter mb-6">
              Engineering meets craftsmanship.
            </h2>
            <p className="text-slate-600 leading-relaxed mb-8">
              Every Tecnografic part is engineered for fit, finished by hand and
              tested against marine conditions. We pair modern automation with
              decades of artisan know-how.
            </p>
            <Link to="/services" className="btn-primary text-xs">
              Explore Capabilities
            </Link>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[color:var(--surface-border)] border border-[color:var(--surface-border)]">
            {items.map((it) => (
              <div key={it.title} className="bg-white p-8 group hover:bg-[color:var(--surface-bg)] transition-colors">
                <div className="w-12 h-12 mb-5 bg-[color:var(--brand-navy)] text-[color:var(--brand-yellow)] flex items-center justify-center group-hover:bg-[color:var(--brand-yellow)] group-hover:text-[color:var(--brand-navy)] transition-colors">
                  <it.icon className="w-5 h-5" />
                </div>
                <h3 className="font-display text-lg font-bold text-[color:var(--brand-navy)] mb-2">
                  {it.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">{it.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const ProcessSteps = () => (
  <section data-testid={HOME.processSteps} className="container-tg py-24 lg:py-32">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
      <div className="lg:col-span-4 lg:sticky lg:top-28">
        <div className="overline mb-4">Process</div>
        <h2 className="font-display text-4xl lg:text-5xl font-extrabold text-[color:var(--brand-navy)] tracking-tighter">
          From brief to bow.
        </h2>
        <p className="mt-5 text-slate-600 leading-relaxed">
          A straightforward path from your idea to a finished, marine-ready
          component delivered to your dock.
        </p>
      </div>
      <ol className="lg:col-span-8 space-y-px bg-[color:var(--surface-border)] border border-[color:var(--surface-border)]">
        {PROCESS.map((p) => (
          <li key={p.step} className="bg-white p-8 lg:p-10 grid grid-cols-12 gap-6 items-start">
            <span className="col-span-2 font-display text-4xl lg:text-5xl font-extrabold text-[color:var(--brand-yellow)] tracking-tighter">
              {p.step}
            </span>
            <div className="col-span-10">
              <h3 className="font-display text-2xl font-bold text-[color:var(--brand-navy)]">{p.title}</h3>
              <p className="mt-2 text-slate-600 leading-relaxed">{p.body}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

const Brands = () => (
  <section className="bg-white border-y border-[color:var(--surface-border)] overflow-hidden">
    <div className="container-tg py-16">
      <div className="text-center mb-10">
        <div className="overline mb-3">Trusted by builders</div>
        <h3 className="font-display text-2xl lg:text-3xl font-bold text-[color:var(--brand-navy)]">
          The largest boat builders rely on Tecnografic.
        </h3>
      </div>
    </div>
    <div className="relative">
      <div className="flex marquee-track gap-12 items-center w-max">
        {[...BRANDS, ...BRANDS].map((b, i) => (
          <div key={`${b.name}-${i}`} className="shrink-0 h-16 w-40 flex items-center justify-center opacity-70 hover:opacity-100 transition-opacity grayscale hover:grayscale-0">
            <img src={b.logo} alt={b.name} className="max-h-full max-w-full object-contain" loading="lazy" />
          </div>
        ))}
      </div>
    </div>
    <div className="container-tg pb-16" />
  </section>
);

const CTASection = () => (
  <section data-testid={HOME.ctaSection} className="bg-[color:var(--brand-navy)] text-white">
    <div className="container-tg py-24 lg:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-8">
          <div className="overline text-[color:var(--brand-yellow)] mb-4">Let's build</div>
          <h2 className="font-display text-4xl lg:text-6xl font-extrabold tracking-tighter leading-[0.95]">
            Ready to bring your <br />
            <span className="text-[color:var(--brand-yellow)]">helm to life?</span>
          </h2>
          <p className="mt-6 text-white/80 max-w-xl leading-relaxed">
            Whether you're a boat builder ramping production or an owner
            refurbishing a beloved vessel — our team is ready to scope, design
            and manufacture your next dashboard.
          </p>
        </div>
        <div className="lg:col-span-4 flex flex-col gap-4">
          <Link to="/contact" className="btn-yellow w-full justify-center">
            Request a Quote <ArrowRight className="w-4 h-4" />
          </Link>
          <a href={`tel:${COMPANY.phone}`} className="text-center text-white/80 hover:text-[color:var(--brand-yellow)] text-sm font-semibold tracking-wide">
            Or call {COMPANY.phone}
          </a>
        </div>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <TrustBar />
      <ServicesGrid />
      <Capabilities />
      <ProcessSteps />
      <Brands />
      <CTASection />
    </>
  );
}
